import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import useWebSocketLib, { ReadyState } from 'react-use-websocket'

import { AuthSessionContext } from '@context/AuthContext'
import { useFeatureFlags } from '@context/FeatureFlagsContext'

import {
  buildNotificationHubWebSocketUrl,
  collectBrowserClientInfo,
  getOrCreateDeviceId,
} from '../core/browserClientInfo'
import { createHandshakePayload, createHeartbeatPayload } from '../core/handshake'
import type { createWebSocketMessageRouter } from '../core/messageRouter'
import { HEARTBEAT_INTERVAL_MS, RECONNECT_ATTEMPTS, RECONNECT_INTERVAL_MS } from '../core/reconnect'
import { useTabCoordinator } from '../core/tabCoordinator'
import type { SendWebSocketJsonMessage, SendWebSocketMessage } from '../core/types'

const preventLastMessageUpdates = () => false

export const useWebSocketTransport = (router: ReturnType<typeof createWebSocketMessageRouter>) => {
  const [auth] = useContext(AuthSessionContext)
  const { websockets, marketDataWSType } = useFeatureFlags()
  const isAuthenticated = Boolean(auth?.accessToken)
  const shouldUseWebSocket =
    isAuthenticated && (websockets || marketDataWSType === 'notifications-hub')
  const coordinator = useTabCoordinator(shouldUseWebSocket)
  const {
    broadcastIncomingMessage,
    broadcastLifecycleEvent,
    broadcastReadyState,
    isLeaderTab,
    leaderTabId,
    requestReadyState,
    sendToLeader,
    subscribe,
    tabId,
  } = coordinator
  const [readyStateFromLeader, setReadyStateFromLeader] = useState(ReadyState.UNINSTANTIATED)
  const heartbeatNumberRef = useRef(0)
  const deviceId = useMemo(() => getOrCreateDeviceId(), [])
  const clientInfo = useMemo(() => collectBrowserClientInfo(deviceId, tabId), [deviceId, tabId])
  const socketUrl = shouldUseWebSocket && isLeaderTab ? buildNotificationHubWebSocketUrl() : null

  const {
    readyState: socketReadyState,
    sendJsonMessage: sendLeaderJsonMessage,
    sendMessage: sendLeaderMessage,
  } = useWebSocketLib(
    socketUrl,
    {
      share: true,
      queryParams:
        process.env.NODE_ENV === 'development' ? { token: auth?.accessToken ?? '' } : undefined,
      onOpen: () => broadcastLifecycleEvent('open'),
      onClose: (event) => broadcastLifecycleEvent('close', event.code),
      onError: () => broadcastLifecycleEvent('error'),
      onReconnectStop: () => broadcastLifecycleEvent('reconnect-stop'),
      onMessage: (event) => {
        const result = router.routeMessage(event.data)
        if (result.message && !result.duplicate) {
          broadcastIncomingMessage(result.message)
        }
      },
      filter: preventLastMessageUpdates,
      reconnectAttempts: RECONNECT_ATTEMPTS,
      reconnectInterval: RECONNECT_INTERVAL_MS,
      shouldReconnect: () => shouldUseWebSocket && isLeaderTab,
    },
    shouldUseWebSocket && isLeaderTab
  )

  useEffect(() => {
    if (!shouldUseWebSocket) return

    return subscribe((message) => {
      switch (message.type) {
        case 'incoming-message':
          router.routeMessage(message.payload.message, { dedupe: false })
          break
        case 'ready-state':
          if (!isLeaderTab) setReadyStateFromLeader(message.payload.readyState)
          break
        case 'ready-state-request':
          if (isLeaderTab) broadcastReadyState(socketReadyState)
          break
        case 'leader-resigned':
          if (!isLeaderTab) setReadyStateFromLeader(ReadyState.CONNECTING)
          break
        case 'outbound-message':
          if (!isLeaderTab) return

          if (message.payload.kind === 'json') {
            sendLeaderJsonMessage(message.payload.message, message.payload.keep)
          } else {
            sendLeaderMessage(message.payload.message, message.payload.keep)
          }
          break
      }
    })
  }, [
    broadcastReadyState,
    isLeaderTab,
    router,
    sendLeaderJsonMessage,
    sendLeaderMessage,
    shouldUseWebSocket,
    socketReadyState,
    subscribe,
  ])

  useEffect(() => {
    if (!shouldUseWebSocket) {
      setReadyStateFromLeader(ReadyState.UNINSTANTIATED)
      return
    }

    if (isLeaderTab) {
      broadcastReadyState(socketReadyState)
      return
    }

    setReadyStateFromLeader(ReadyState.CONNECTING)
    if (leaderTabId) requestReadyState()
  }, [
    broadcastReadyState,
    isLeaderTab,
    leaderTabId,
    requestReadyState,
    shouldUseWebSocket,
    socketReadyState,
  ])

  useEffect(() => {
    if (!shouldUseWebSocket || !isLeaderTab || socketReadyState !== ReadyState.OPEN) return

    sendLeaderJsonMessage(createHandshakePayload(clientInfo), false)
    heartbeatNumberRef.current = 0

    const intervalId = window.setInterval(() => {
      heartbeatNumberRef.current += 1
      sendLeaderJsonMessage(createHeartbeatPayload(heartbeatNumberRef.current), false)
    }, HEARTBEAT_INTERVAL_MS)

    return () => window.clearInterval(intervalId)
  }, [clientInfo, isLeaderTab, sendLeaderJsonMessage, shouldUseWebSocket, socketReadyState])

  const sendMessage = useCallback<SendWebSocketMessage>(
    (message, keep = true) => {
      if (!shouldUseWebSocket) return

      if (isLeaderTab) {
        sendLeaderMessage(message, keep)
      } else {
        sendToLeader({ kind: 'text', message, keep })
      }
    },
    [isLeaderTab, sendLeaderMessage, sendToLeader, shouldUseWebSocket]
  )

  const sendJsonMessage = useCallback<SendWebSocketJsonMessage>(
    (message, keep = true) => {
      if (!shouldUseWebSocket) return

      if (isLeaderTab) {
        sendLeaderJsonMessage(message, keep)
      } else {
        sendToLeader({ kind: 'json', message, keep })
      }
    },
    [isLeaderTab, sendLeaderJsonMessage, sendToLeader, shouldUseWebSocket]
  )

  const readyState = shouldUseWebSocket
    ? isLeaderTab
      ? socketReadyState
      : readyStateFromLeader
    : ReadyState.UNINSTANTIATED

  return {
    coordinator,
    enabled: shouldUseWebSocket,
    readyState,
    sendJsonMessage,
    sendLeaderJsonMessage,
    sendMessage,
    socketReadyState,
  }
}

export type WebSocketTransport = ReturnType<typeof useWebSocketTransport>
